import { styled } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';

import { bindCardSelectionDisabled, useSwimlaneRootProps } from '@/components/ui/swimlane/context';
import {
  AXIS_HEIGHT,
  COLUMN_HEADER_HEIGHT,
  GRID_GAP,
  ROW_HEADER_WIDTH,
} from '@/components/ui/swimlane/layout/constants';

const MIN_WIDTH = ROW_HEADER_WIDTH.horizontal.value;
const MAX_WIDTH = 600;

function getBoundedWidth(cursor: number): number {
  const widths = [MIN_WIDTH, cursor, MAX_WIDTH];
  widths.sort((a, b) => a - b);
  return widths[1] ?? MIN_WIDTH;
}

interface IResize {
  rowHeaderWidth: number;
  offset: number;
}

export function Resize({ rowHeaderWidth, offset }: IResize) {
  const { columnAxisLabelText, rowHeaderOrientation, readOnly, actions } = useSwimlaneRootProps();
  const [resizerPosition, setResizerPosition] = useState(rowHeaderWidth + offset);
  const [inResizeMode, setInResizeMode] = useState(false);
  const mouseRef = useRef(rowHeaderWidth + offset);
  const doAnimationRef = useRef(false);

  useEffect(() => {
    const totalWidth = rowHeaderWidth + offset;
    setResizerPosition(totalWidth);
    mouseRef.current = totalWidth;
  }, [rowHeaderWidth, offset]);

  const handleMouseDown = () => {
    doAnimationRef.current = true;
    setInResizeMode(true);
    requestAnimationFrame(animate);
  };

  const handleMouseUp = () => {
    const boundedPosition = getBoundedWidth(mouseRef.current);
    if (boundedPosition !== undefined) {
      mouseRef.current = boundedPosition;
      const newRowHeaderWidth = boundedPosition - offset;
      if (doAnimationRef.current && newRowHeaderWidth !== rowHeaderWidth) {
        // fire action to save the new value
        actions?.headerActions.setRowHeaderWidth?.(newRowHeaderWidth);
      }
    }
    doAnimationRef.current = false;
    setInResizeMode(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!doAnimationRef.current) return;
    mouseRef.current = e.clientX - 6; // -6 adjusts for cursor width, centering the column with the cursor
  };

  const animate = () => {
    if (doAnimationRef.current) {
      requestAnimationFrame(animate);
    }
    setResizerPosition(getBoundedWidth(mouseRef.current ?? 0));
  };

  if (readOnly) return null;
  if (rowHeaderOrientation === 'vertical') return null;

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: this overlay is not user-interactive
    <div
      data-testid="resize-container"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      style={{
        cursor: 'col-resize',
        position: 'fixed',
        top: 0,
        bottom: 0,
        ...(inResizeMode && { left: 0, right: 0 }),
      }}
    >
      <Resizer
        role="slider"
        aria-valuenow={resizerPosition}
        aria-label="Adjust the Row Label Width"
        onMouseDown={handleMouseDown}
        style={{
          left: `${mouseRef.current}px`,
          top:
            typeof columnAxisLabelText === 'string'
              ? `${AXIS_HEIGHT.value + COLUMN_HEADER_HEIGHT.value + GRID_GAP.value * 2}px`
              : `${COLUMN_HEADER_HEIGHT.value + GRID_GAP.value}px`,
        }}
        {...bindCardSelectionDisabled()}
      />
    </div>
  );
}

const Resizer = styled('div')(({ theme }) => ({
  // 4 (width) + 6 (margin left) + 6 (margin right) = GRID_GAP
  width: '4px',
  position: 'fixed',
  bottom: 0,
  zIndex: 100,
  margin: '0 6px',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
  },
  '&:active': {
    backgroundColor: theme.palette.primary.dark,
  },
}));
